import { useEffect, useRef, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

import {
  track,
  capturePageview,
  trackStep,
} from "@/lib/posthog";
import { captureSentryException } from "@/lib/sentry";
import { trackBrevo, touchActivity } from "@/lib/brevo";
import { fireSignupConversion } from "@/lib/gtag";
import PhotoUpload from "@/components/PhotoUpload";
import Questionnaire, { type Answers } from "@/components/Questionnaire";
import { useGenerationQuota, formatResetDate } from "@/hooks/useGenerationQuota";
import { useSubscription } from "@/hooks/useSubscription";
import { startProCheckout } from "@/lib/billing";
import PromoUpsell from "@/components/PromoUpsell";
import UpsellDialog from "@/components/UpsellDialog";
import AppShell from "@/components/AppShell";
import { resolveUser } from "@/lib/session";


type Step = "upload" | "questions";

export type DebugInfo = {
  step1?: string;
  step2?: string;
  anchors?: string[];
  room_size?: {
    sqm?: number | null;
    source?: "user" | "estimated" | "unknown";
    confidence?: string | null;
    basis?: string | null;
    raw?: string | null;
  } | null;
  variants?: { id: string; label: string; prompt: string; image?: string | null }[];
};

export type ExistingItem = {
  name: string;
  decision: "keep" | "replace";
  reasoning: string;
};

function fireBrevoLimitHitOnce(params: {
  used: number;
  limit: number;
  resetAt: Date | null;
  surface: string;
}) {
  try {
    const now = new Date();
    const periodKey = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, "0")}`;
    const storageKey = `huelo:brevo-limit-hit:${periodKey}`;
    if (typeof localStorage === "undefined") return;
    if (localStorage.getItem(storageKey)) return;
    localStorage.setItem(storageKey, "1");
    void trackBrevo("monthly_limit_reached", {
      used: params.used,
      limit: params.limit,
      reset_at: params.resetAt?.toISOString() ?? null,
      plan: "free",
      surface: params.surface,
    });
    touchActivity();
  } catch {
    // never break UI on analytics
  }
}

const Index = () => {
  const [step, setStep] = useState<Step>("upload");
  const [preview, setPreview] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [upgrading, setUpgrading] = useState(false);

  const { toast } = useToast();
  const quota = useGenerationQuota();
  const [hardWallTick, setHardWallTick] = useState(0);
  const { isPro, promoEligible } = useSubscription();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const presetStyle = searchParams.get("style")?.slice(0, 40) || undefined;

  const handleUpgrade = async () => {
    setUpgrading(true);
    try {
      const url = await startProCheckout("month");
      window.open(url, "_blank");
    } catch (e: any) {
      toast({ title: "Checkout failed", description: e?.message, variant: "destructive" });
    } finally {
      setUpgrading(false);
    }
  };

  const previousStepRef = useRef<Step | null>(null);
  const stepEnteredAtRef = useRef<number>(
    typeof performance !== "undefined" ? performance.now() : 0,
  );

  useEffect(() => {
    const now = typeof performance !== "undefined" ? performance.now() : 0;
    const previous = previousStepRef.current;
    const elapsed = previous ? now - stepEnteredAtRef.current : 0;
    capturePageview(`/app/${step}`);
    trackStep(step, previous, elapsed);
    previousStepRef.current = step;
    stepEnteredAtRef.current = now;
  }, [step]);

  // Fire Google Ads SIGNUP conversion once, only for users whose profile
  // was created within the last 10 minutes (i.e. genuine new signups).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: userData } = await supabase.auth.getUser();
      const user = userData.user;
      if (!user || cancelled) return;
      const { data: profile } = await supabase
        .from("profiles")
        .select("created_at")
        .eq("user_id", user.id)
        .maybeSingle();
      if (cancelled || !profile?.created_at) return;
      const ageMs = Date.now() - new Date(profile.created_at).getTime();
      if (ageMs < 10 * 60 * 1000) {
        const fired = fireSignupConversion(user.id);
        if (fired) {
          track("signup_conversion_fired", {
            provider: "google_ads",
            profile_age_ms: ageMs,
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleUpload = (_file: File, previewUrl: string) => {
    setPreview(previewUrl);
    setStep("questions");
  };

  // On questionnaire submit: create a pending design_history row, navigate to
  // its unique URL, and kick off generation in the background. The edge
  // function updates the same row when it finishes (or fails), so the design
  // page can display the result even if the user refreshes or comes back
  // later. This gives every final output the same shareable URL as past
  // designs (/dashboard/design/:id).
  const handleComplete = async (a: Answers) => {
    if (submitting) return;

    if (quota.remaining <= 0 && !quota.loading && !quota.unlimited) {
      track("paywall_hit", { limit: "designs", placement: "new_design", promo_eligible: promoEligible, used: quota.used, quota_limit: quota.limit });
      track("monthly_quota_exceeded", {
        used: quota.used,
        limit: quota.limit,
        reset_at: quota.resetAt?.toISOString() ?? null,
        surface: "questionnaire_submit",
      });
      toast({
        title: "Monthly limit reached",
        description: `You've used all ${quota.limit} designs this month. Resets ${formatResetDate(quota.resetAt)}.`,
        variant: "destructive",
      });
      if (!isPro) setHardWallTick((n) => n + 1);
      return;
    }

    setSubmitting(true);
    try {
      const user = await resolveUser();
      if (!user) {
        await supabase.auth.signOut().catch(() => {});
        toast({
          title: "Your session expired",
          description: "Please sign in again — your answers are still here.",
          variant: "destructive",
        });
        navigate("/", { state: { redirectTo: "/app" } });
        setSubmitting(false);
        return;
      }


      // Upload the source photo to storage first so the pending row has a
      // real preview_url. Without this, refreshing the generating screen or
      // viewing the pending row in the past-projects list shows a broken
      // image because the in-memory blob/data URL only lives in this tab.
      let uploadedPreviewUrl: string | null = null;
      try {
        const res = await fetch(preview);
        const blob = await res.blob();
        const ext = (blob.type.split("/")[1] || "jpg").replace("jpeg", "jpg");
        const path = `${user.id}/${crypto.randomUUID()}/original.${ext}`;
        const { error: upErr } = await supabase.storage
          .from("design-images")
          .upload(path, blob, { contentType: blob.type || "image/jpeg", upsert: true });
        if (upErr) {
          console.warn("Preview upload failed; continuing without preview_url:", upErr);
        } else {
          uploadedPreviewUrl = supabase.storage.from("design-images").getPublicUrl(path).data.publicUrl;
        }
      } catch (e) {
        console.warn("Preview upload threw; continuing without preview_url:", e);
      }

      const { data: inserted, error: insErr } = await supabase
        .from("design_history")
        .insert({
          user_id: user.id,
          answers: a as any,
          status: "pending",
          preview_url: uploadedPreviewUrl,
        })
        .select("id")
        .single();
      if (insErr) throw insErr;

      const designId = inserted.id;

      const genRequestedProps = {
        design_id: designId,
        generation_type: "initial" as const,
        is_regen: false,
        source: "new_design",
        has_preview_url: !!uploadedPreviewUrl,
      };
      console.debug("[track] generation_requested", genRequestedProps);
      track("generation_requested", genRequestedProps);
      void trackBrevo("image_generated", {
        design_id: designId,
        generation_type: "initial",
      });
      touchActivity();

      // If this generation just brought a free user to their monthly cap,
      // fire the Brevo limit-hit event exactly at the moment of hitting it.
      if (!isPro && !quota.unlimited && quota.used + 1 >= quota.limit) {
        fireBrevoLimitHitOnce({
          used: quota.used + 1,
          limit: quota.limit,
          resetAt: quota.resetAt,
          surface: "generation_completed_cap",
        });
      }

      // Mark the row failed so the design page leaves the "generating"
      // state instead of spinning forever, and emit generation_failed so
      // analytics sees every non-completing attempt.
      const failDesign = async (rawError: unknown, errorType: string) => {
        const message =
          (rawError as any)?.message ||
          (typeof rawError === "string" ? rawError : "") ||
          "Something went wrong. Please try again.";
        track("generation_failed", {
          design_id: designId,
          error_type: errorType,
          error_message: String(message).slice(0, 300),
          generation_type: "initial",
          is_regen: false,
          surface: "invoke_client",
        });
        try {
          await supabase
            .from("design_history")
            .update({ status: "failed", error_message: String(message).slice(0, 500) })
            .eq("id", designId)
            .eq("status", "pending");
        } catch (e) {
          console.warn("Couldn't mark design failed:", e);
        }
      };

      // A FunctionsFetchError means the browser's fetch was dropped (tab
      // backgrounded, network switch, sleep) — the edge function is very
      // likely still running server-side. Poll the row before declaring
      // failure so we don't kill a generation that ends up succeeding.
      const failDesignUnlessCompletes = async (rawError: unknown) => {
        const deadline = Date.now() + 6 * 60 * 1000;
        while (Date.now() < deadline) {
          await new Promise((r) => setTimeout(r, 5000));
          const { data: row } = await supabase
            .from("design_history")
            .select("status")
            .eq("id", designId)
            .maybeSingle();
          if (!row || row.status !== "pending") return;
        }
        await failDesign(rawError, "invoke_threw");
      };

      // Kick off generation in the background. The edge function will
      // UPDATE the row when it finishes; DesignDetail polls for the change.
      // Not awaited — we navigate immediately so the user sees the
      // generating state on the resumable URL.
      void supabase.functions
        .invoke("generate-room-design", {
          body: { image: preview, answers: a, designId },
        })
        .then(async ({ data, error }) => {
          if (error || data?.error) {
            let genErr: unknown = error || data?.error;
            // Pull the server's real message out of non-2xx responses instead
            // of the generic "Edge Function returned a non-2xx status code".
            try {
              const ctx = (error as any)?.context;
              if (ctx && typeof ctx.json === "function") {
                const body = await ctx.clone().json().catch(() => null);
                if (body?.error) genErr = `${body.error} (HTTP ${ctx.status})`;
              }
            } catch { /* keep original */ }
            console.error("generate-room-design failed:", genErr);
            captureSentryException(genErr, { design_id: designId, source: "generate-room-design" });
            void failDesign(genErr, "edge_function_error");
            return;
          }
          if (!data?.image) {
            console.error("generate-room-design returned no image", data);
            void failDesign("No image was returned.", "no_image_returned");
            return;
          }
          // Kick off product matching once generation succeeds. Runs
          // separately so the primary image lands fast; results persist to
          // the same design_history row.
          void supabase.functions.invoke("match-design-products", {
            body: {
              aiImage: data.image,
              generationId: data.generationId ?? null,
              designId,
              answers: a,
              existingItems: Array.isArray(data.existingItems) ? data.existingItems : undefined,
            },
          });
        })
        .catch((err) => {
          console.error("generate-room-design invoke threw:", err);
          captureSentryException(err, { design_id: designId, source: "generate-room-design-invoke" });
          void failDesignUnlessCompletes(err);
        });

      void quota.refresh();
      navigate(`/dashboard/design/${designId}`, {
        replace: true,
        state: { preview, kickoff: true },
      });
    } catch (err: any) {
      console.error("Failed to start generation:", err);
      captureSentryException(err, { source: "generation-setup" });
      toast({
        title: "Couldn't start generation",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
      setSubmitting(false);
    }
  };

  return (
    <AppShell>
      <UpsellDialog type="hard_wall" active={hardWallTick > 0 && promoEligible} trigger={hardWallTick} promoEligible={promoEligible} />
      <div className="flex-1 flex items-center justify-center px-5 py-12">
        <AnimatePresence mode="wait">
          {step === "upload" && (
            <div key="upload" className="w-full flex flex-col items-center gap-4">
              {!quota.loading && !quota.unlimited && !isPro && (
                <PromoUpsell
                  placement="design_banner"
                  promoEligible={promoEligible}
                  quotaRemaining={quota.remaining}
                  className="w-full max-w-xl lg:max-w-5xl mb-2"
                  title={quota.remaining <= 0 ? "You've reached your monthly design limit" : undefined}
                />
              )}
              <PhotoUpload onUpload={handleUpload} />
              {!quota.loading && (
                <p className="text-xs text-muted-foreground text-center">
                  {quota.unlimited
                    ? "Unlimited designs · Huelo team account"
                    : quota.remaining > 0
                    ? `${quota.remaining} of ${quota.limit} designs left this month · resets ${formatResetDate(quota.resetAt)}`
                    : `You've used all ${quota.limit} designs this month. Resets ${formatResetDate(quota.resetAt)}.`}
                </p>
              )}
            </div>
          )}
          {step === "questions" && (
            <Questionnaire
              key="questions"
              preview={preview}
              onComplete={handleComplete}
              onBack={() => setStep("upload")}
              initialStyle={presetStyle}
            />
          )}
        </AnimatePresence>
      </div>
    </AppShell>
  );
};

export default Index;
